import { motion } from 'framer-motion'

/**
 * Introduction Component
 * Restructured content from Hero explaining the company's approach
 */
const Introduction = () => {
  const sectors = [
    {
      title: 'Pharmaceuticals',
      description: 'Accuracy is life-critical. We create artwork aligned with FDA, CDSCO, EU, and GCC regulations, ensuring every panel, leaflet, and label meets the highest global standards.',
      icon: (
        <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
        </svg>
      ),
      color: 'from-blue-500 to-blue-600',
    },
    {
      title: 'Food',
      description: 'Clarity builds confidence. From nutritional panels to allergen declarations, we create compliant, multilingual packaging that informs, protects, and earns consumer trust across diverse markets.',
      icon: (
        <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      ),
      color: 'from-green-500 to-green-600',
    },
    {
      title: 'FMCG',
      description: 'Speed and consistency define success. We help brands scale internationally with artwork that is visually unified, locally adapted, and always production ready — no matter the volume or timeline.',
      icon: (
        <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      color: 'from-orange-500 to-orange-600',
    },
    {
      title: 'Global Markets',
      description: 'Localization drives relevance. With deep expertise in multilingual packaging — including Arabic — we ensure your brand voice stays consistent, compliant, and culturally accurate across borders.',
      icon: (
        <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      color: 'from-purple-500 to-purple-600',
    },
  ]

  const differentiators = [
    {
      title: 'Regulatory Precision',
      description: 'Global standards translated into accurate, audit‑ready artwork — CDSCO, FDA, EU, GCC and beyond.',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
    },
    {
      title: 'Multilingual Expertise',
      description: 'Arabic, French, Hindi, Spanish — packaging that communicates clearly in every market.',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
        </svg>
      ),
    },
    {
      title: 'Brand Integrity',
      description: "Consistent visuals across SKUs, regions, and formats, preserving your brand's global identity.",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
        </svg>
      ),
    },
    {
      title: 'Strategic Visual Systems',
      description: 'Layouts, icons, and hierarchies engineered for clarity, impact, and seamless storytelling.',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
    },
  ]

  return (
    <section
      id="introduction"
      className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-white via-primary-50/30 to-white relative overflow-hidden border-t border-gray-200/50"
      aria-labelledby="introduction-heading"
    >
      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-primary-200/20 rounded-full blur-3xl -z-10"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary-300/20 rounded-full blur-3xl -z-10"></div>
      
      {/* Top decorative divider */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary-200/50 to-transparent"></div>

      <div className="container mx-auto max-w-7xl relative z-10">
        {/* Section Header with Strong Title */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2
            id="introduction-heading"
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight"
          >
            Building Trust Through{' '}
            <span className="bg-gradient-to-r from-primary-600 to-primary-800 bg-clip-text text-transparent">
              Precision & Compliance
            </span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-primary-500 to-primary-700 mx-auto mb-8"></div>
        </motion.div>

        {/* Main Introduction Text */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="max-w-4xl mx-auto text-center mb-20"
        >
          <p className="text-xl md:text-2xl text-gray-800 leading-relaxed mb-6 font-medium">
            At Artwork Solution, packaging isn't just a container — it's a system of trust. A system built on accuracy, shaped by regulation, strengthened by design, and ready for global markets.
          </p>
          <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
            We operate as a specialized packaging artwork consultancy four high-stakes sectors:
          </p>
        </motion.div>

        {/* Four Sectors Grid - Enhanced Design */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-24">
          {sectors.map((sector, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50, scale: 0.9 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ y: -12, scale: 1.02, transition: { duration: 0.3 } }}
              className="group relative"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-lg group-hover:shadow-2xl transition-all duration-500 border-2 border-gray-100 group-hover:border-primary-300 transform group-hover:-rotate-1 group-hover:scale-[1.02]"></div>
              <div className="relative bg-white rounded-3xl p-8 h-full flex flex-col overflow-hidden">
                {/* Animated background on hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${sector.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}></div>
                {/* Icon with gradient background */}
                <div className={`relative inline-flex p-4 rounded-2xl bg-gradient-to-br ${sector.color} mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg group-hover:shadow-xl`}>
                  <div className="text-white">
                    {sector.icon}
                  </div>
                </div>
                <h3 className="relative text-xl font-bold text-gray-900 mb-4 group-hover:text-primary transition-colors duration-300">{sector.title}</h3>
                <p className="relative text-gray-600 leading-relaxed text-sm flex-1 group-hover:text-gray-700 transition-colors duration-300">{sector.description}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Approach Statement - Enhanced */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="max-w-5xl mx-auto mb-20"
        >
          <div className="bg-gradient-to-br from-primary-900 to-primary-800 rounded-3xl p-12 text-white shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl"></div>
            <div className="relative z-10 space-y-6">
              <p className="text-lg md:text-xl leading-relaxed">
                With a foundation in regulatory compliance, brand adaptation, and multilingual localization, we combine technical rigor with design intelligence.
              </p>
              <p className="text-lg md:text-xl leading-relaxed font-medium">
                Our approach is collaborative, detail‑driven, and engineered for scale — whether you're launching a new SKU or harmonizing a global portfolio.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Key Differentiators - Enhanced Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {differentiators.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              whileHover={{ y: -8, scale: 1.02, transition: { duration: 0.3 } }}
              className="group bg-white rounded-2xl p-6 border-2 border-gray-100 hover:border-primary-300 hover:shadow-xl transition-all duration-300 relative overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-primary-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative text-primary mb-4 group-hover:scale-110 group-hover:rotate-6 transition-transform duration-300">{item.icon}</div>
              <h4 className="relative font-bold text-lg mb-3 text-gray-900 group-hover:text-primary transition-colors duration-300">{item.title}:</h4>
              <p className="relative text-gray-600 text-sm leading-relaxed group-hover:text-gray-700 transition-colors duration-300">{item.description}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Closing Statement - Enhanced */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-20 text-center"
        >
          <div className="inline-block bg-gradient-to-r from-primary-600 to-primary-800 text-white px-8 py-4 rounded-full shadow-lg">
            <p className="text-xl md:text-2xl font-bold">
              Artwork Solution — Trusted compliance for a borderless market.
            </p>
          </div>
        </motion.div>
      </div>
      
      {/* Bottom decorative divider */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-300/50 to-transparent"></div>
    </section>
  )
}

export default Introduction
